import { SubmissionForm } from './SubmissionForm';
import { externalSyncStatus, type ExternalJob } from './sync-status';
import './submissions.css';
import { useRef, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { callCore, mutateCore } from '../lib/rpc';
import { t } from '../messages';
import { useDesk } from '../desk/context';
import { externalRequestKey } from '../teacher/external-model';
import './projection.css';
interface RecordSummary {
  kind: string;
  externalId: string;
  title: string;
  status: string;
}
interface Projection {
  sourceVersion: string;
  revision: number;
  observedAt: string;
  collections: { kind: string; label: string }[];
  total: number;
  items: RecordSummary[];
  selected: (RecordSummary & { fields: { key: string; label: string; value: string }[] }) | null;
}
export function ProjectionPanel({
  environmentId,
  canSync,
  jobs,
  jobsUnavailable,
  onQueued,
}: {
  environmentId: string;
  canSync: boolean;
  jobs: readonly ExternalJob[];
  jobsUnavailable: boolean;
  onQueued: () => Promise<unknown>;
}) {
  const { me, observing, workspace } = useDesk();
  const [open, setOpen] = useState(false),
    [collection, setCollection] = useState(''),
    [search, setSearch] = useState(''),
    [offset, setOffset] = useState(0),
    [selected, setSelected] = useState<{ kind: string; externalId: string }>(),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [acknowledgedId, setAcknowledgedId] = useState<string | null>(null);
  const key = useRef<string | undefined>(undefined);
  const query = useQuery({
    queryKey: ['core', 'external.projection', environmentId, collection, search, offset, selected],
    enabled: open,
    placeholderData: keepPreviousData,
    queryFn: () =>
      callCore<{ projection: Projection | null }>('external.projection', {
        environmentId,
        ...(collection ? { collection } : {}),
        search,
        offset,
        selected,
      }),
    refetchInterval: open ? 5000 : false,
  });
  const projection = query.data?.projection;
  const syncStatus = externalSyncStatus(jobs, acknowledgedId, jobsUnavailable);
  const syncing = syncStatus === 'queued' || syncStatus === 'running';
  async function sync() {
    if (!canSync || busy || syncing || jobsUnavailable || observing || workspace?.readOnly) return;
    const scope = `ndrc-cms-sync:${me.id}:${environmentId}`;
    let requestKey = key.current ?? crypto.randomUUID();
    try {
      requestKey = externalRequestKey(sessionStorage, scope, () => requestKey);
    } catch {
      /* Memory retry stays available. */
    }
    key.current = requestKey;
    setBusy(true);
    setError('');
    try {
      const accepted = await mutateCore<{ id: string }>(
        'external.command',
        { environmentId, action: 'sync', requestKey },
        requestKey,
      );
      setAcknowledgedId(accepted.id);
      key.current = undefined;
      try {
        sessionStorage.removeItem(scope);
      } catch {
        /* Optional storage. */
      }
      await Promise.all([onQueued(), query.refetch()]);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : t('cms.projection.error'));
    } finally {
      setBusy(false);
    }
  }
  return (
    <details className="cms-projection" onToggle={(event) => setOpen(event.currentTarget.open)}>
      <summary>{t('cms.projection.title')}</summary>
      {open && (
        <div className="cms-projection-body">
          <p className="cms-projection-help">{t('cms.projection.help')}</p>
          <div className="cms-projection-toolbar">
            <button
              type="button"
              className="ndrc-button"
              disabled={
                !canSync ||
                busy ||
                syncing ||
                jobsUnavailable ||
                Boolean(observing) ||
                workspace?.readOnly
              }
              onClick={() => {
                void sync();
              }}
            >
              {busy ? t('cms.working') : t('cms.projection.sync')}
            </button>
            <button
              type="button"
              className="ndrc-button"
              disabled={query.isFetching}
              onClick={() => {
                setError('');
                void Promise.all([query.refetch(), onQueued()]).catch(() => {
                  setError(t('cms.projection.followupError'));
                });
              }}
            >
              {t('cms.refresh')}
            </button>
            {projection && (
              <span>
                {t('cms.projection.observed', {
                  date: new Intl.DateTimeFormat('fr-FR', {
                    timeZone: 'Europe/Paris',
                    dateStyle: 'short',
                    timeStyle: 'short',
                  }).format(Date.parse(projection.observedAt)),
                  revision: projection.revision,
                })}
              </span>
            )}
          </div>
          {!canSync && !syncing && !jobsUnavailable && (
            <p className="cms-projection-help">{t('cms.projection.startHint')}</p>
          )}
          {(error || query.isError) && (
            <p className="ndrc-error" role="alert">
              {error || query.error?.message}
            </p>
          )}
          {syncStatus && !busy && (
            <p
              className={
                syncStatus === 'failed' || syncStatus === 'unknown'
                  ? 'ndrc-error'
                  : 'cms-projection-help'
              }
              role={syncStatus === 'failed' || syncStatus === 'unknown' ? 'alert' : 'status'}
            >
              {t(`cms.projection.status.${syncStatus}`)}
            </p>
          )}
          {query.isPending ? (
            <p role="status">{t('cms.loading')}</p>
          ) : !projection ? (
            <p>{t('cms.projection.empty')}</p>
          ) : (
            <>
              <div className="cms-projection-filters">
                <label>
                  {t('cms.projection.collection')}
                  <select
                    value={collection}
                    onChange={(event) => {
                      setCollection(event.target.value);
                      setOffset(0);
                      setSelected(undefined);
                    }}
                  >
                    <option value="">{t('cms.projection.all')}</option>
                    {projection.collections.map((item) => (
                      <option key={item.kind} value={item.kind}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  {t('cms.projection.search')}
                  <input
                    type="search"
                    value={search}
                    onChange={(event) => {
                      setSearch(event.target.value);
                      setOffset(0);
                      setSelected(undefined);
                    }}
                  />
                </label>
              </div>
              <div className="cms-projection-grid">
                <div>
                  <p className="cms-projection-count">
                    {t(
                      projection.total === 1 ? 'cms.projection.countOne' : 'cms.projection.count',
                      {
                        count: projection.total,
                      },
                    )}
                  </p>
                  {projection.items.length === 0 ? (
                    <p>{t('cms.projection.noResults')}</p>
                  ) : (
                    <ul className="cms-projection-records">
                      {projection.items.map((item) => (
                        <li key={`${item.kind}:${item.externalId}`}>
                          <button
                            type="button"
                            aria-pressed={
                              selected?.kind === item.kind &&
                              selected.externalId === item.externalId
                            }
                            onClick={() =>
                              setSelected({ kind: item.kind, externalId: item.externalId })
                            }
                          >
                            <strong>{item.title || t('cms.projection.untitled')}</strong>
                            <span>{item.status}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <nav className="cms-projection-pages" aria-label={t('cms.projection.pages')}>
                    <button
                      type="button"
                      className="ndrc-button"
                      disabled={offset === 0}
                      onClick={() => setOffset(Math.max(0, offset - 20))}
                    >
                      {t('cms.projection.previous')}
                    </button>
                    <span>{t('cms.projection.page', { page: Math.floor(offset / 20) + 1 })}</span>
                    <button
                      type="button"
                      className="ndrc-button"
                      disabled={offset + 20 >= projection.total}
                      onClick={() => setOffset(offset + 20)}
                    >
                      {t('cms.projection.next')}
                    </button>
                  </nav>
                </div>
                <section className="cms-projection-detail" aria-label={t('cms.projection.detail')}>
                  {projection.selected ? (
                    <>
                      <h4>{projection.selected.title || t('cms.projection.untitled')}</h4>
                      <p>{projection.selected.status}</p>
                      <dl>
                        {projection.selected.fields.map((field) => (
                          <div key={field.key}>
                            <dt>{field.label}</dt>
                            <dd>{field.value || t('cms.projection.unset')}</dd>
                          </div>
                        ))}
                      </dl>
                      {me.role === 'student' && !observing && (
                        <SubmissionForm
                          key={`${environmentId}:${projection.selected.kind}:${projection.selected.externalId}`}
                          environmentId={environmentId}
                          kind={projection.selected.kind}
                          externalId={projection.selected.externalId}
                          sourceVersion={projection.sourceVersion}
                        />
                      )}
                    </>
                  ) : (
                    <p>{t('cms.projection.choose')}</p>
                  )}
                </section>
              </div>
            </>
          )}
        </div>
      )}
    </details>
  );
}
